/**
 * SSE JSON wire format (parsed by browser and API).
 * Redis channel names and throttle keys: backend/src/realtime/redis-channels.ts
 */
import { z } from 'zod';
import { containerSlotScanJobPhaseSchema } from '../container-slot-scan.schemas.js';
import { batchWaferTransferJobPhaseSchema } from '../batch-wafer-transfer.schemas.js';
import { fingerprintInferenceBatchPhaseSchema } from '../fingerprint-inference.schemas.js';

export const containerSlotScanSseJobProgressSchema = z.object({
  type: z.literal('job_progress'),
  jobId: z.string(),
  phase: containerSlotScanJobPhaseSchema,
  completedSlots: z.number().int().min(0),
  totalSlots: z.number().int().min(0),
  revision: z.number().int(),
  at: z.string(),
});

export const containerSlotScanSseJobTerminalSchema = z.object({
  type: z.literal('job_terminal'),
  jobId: z.string(),
  phase: containerSlotScanJobPhaseSchema,
  at: z.string(),
});

export const containerSlotScanSseEventSchema = z.discriminatedUnion('type', [
  containerSlotScanSseJobProgressSchema,
  containerSlotScanSseJobTerminalSchema,
]);

export type ContainerSlotScanSseEvent = z.infer<typeof containerSlotScanSseEventSchema>;

export const batchWaferTransferSseJobProgressSchema = z.object({
  type: z.literal('job_progress'),
  jobId: z.string(),
  phase: batchWaferTransferJobPhaseSchema,
  completedTransfers: z.number().int().min(0),
  totalTransfers: z.number().int().min(0),
  revision: z.number().int(),
  at: z.string(),
});

export const batchWaferTransferSseTransferItemCompleteSchema = z.object({
  type: z.literal('transfer_item_complete'),
  jobId: z.string(),
  transferId: z.string(),
  at: z.string(),
  outcome: z.string().optional(),
});

export const batchWaferTransferSseJobTerminalSchema = z.object({
  type: z.literal('job_terminal'),
  jobId: z.string(),
  phase: batchWaferTransferJobPhaseSchema,
  at: z.string(),
});

export const batchWaferTransferSseEventSchema = z.discriminatedUnion('type', [
  batchWaferTransferSseJobProgressSchema,
  batchWaferTransferSseTransferItemCompleteSchema,
  batchWaferTransferSseJobTerminalSchema,
]);

export type BatchWaferTransferSseEvent = z.infer<typeof batchWaferTransferSseEventSchema>;

export const efemLockChangedActionSchema = z.enum([
  'acquired',
  'released',
  'extended',
  'force_released',
]);

export const efemLockSseEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('lock_changed'),
    efemClientId: z.string(),
    action: efemLockChangedActionSchema,
    at: z.string(),
  }),
  z.object({
    type: z.literal('lock_expired'),
    efemClientId: z.string(),
    at: z.string(),
  }),
]);

export type EfemLockSseEvent = z.infer<typeof efemLockSseEventSchema>;

export const fingerprintInferenceSseBatchProgressSchema = z.object({
  type: z.literal('batch_progress'),
  batchId: z.string().uuid(),
  phase: fingerprintInferenceBatchPhaseSchema,
  completedImages: z.number().int().min(0),
  totalImages: z.number().int().min(0),
  revision: z.number().int(),
  at: z.string(),
});

export const fingerprintInferenceSseBatchTerminalSchema = z.object({
  type: z.literal('batch_terminal'),
  batchId: z.string().uuid(),
  phase: fingerprintInferenceBatchPhaseSchema,
  at: z.string(),
});

export const fingerprintInferenceSseEventSchema = z.discriminatedUnion('type', [
  fingerprintInferenceSseBatchProgressSchema,
  fingerprintInferenceSseBatchTerminalSchema,
]);

export type FingerprintInferenceSseEvent = z.infer<typeof fingerprintInferenceSseEventSchema>;
